import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { IndiaMap } from "../components/IndiaMap";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CATEGORIES, INDIAN_STATES } from "../lib/constants";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");
import { MapPin, ArrowRight, Plus, Lock } from "lucide-react";

const SignUpPrompt = ({ state }) => (
  <div className="rounded-lg bg-secondary p-4" data-testid="signup-prompt">
    <div className="flex items-center gap-2 font-medium text-primary"><Lock className="h-4 w-4" /> Members only</div>
    <p className="text-sm text-muted-foreground mt-1">Sign up or log in to view listings in {state}.</p>
    <div className="mt-3 flex gap-2">
      <Link to="/register"><Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="signup-prompt-register">Sign up</Button></Link>
      <Link to="/login"><Button size="sm" variant="outline" data-testid="signup-prompt-login">Log in</Button></Link>
    </div>
  </div>
);

const StateListings = ({ state, category }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let ignore = false;
    setItems(null);
    const params = { state, limit: 6 };
    if (category !== "all") params.category = category;
    api.get("/listings", { params })
      .then(({ data }) => { if (!ignore) setItems(data.items); })
      .catch(() => { if (!ignore) setItems("error"); });
    return () => { ignore = true; };
  }, [state, category]);

  if (items === "error") return <div className="text-sm text-destructive py-6" data-testid="state-error">Could not load listings. Please try again.</div>;
  if (!items) return <div className="text-sm text-muted-foreground py-6">Loading…</div>;
  if (!items.length) return <div className="text-sm text-muted-foreground py-6" data-testid="state-empty">No listings in {state} yet.</div>;
  return (
    <ul className="space-y-2" data-testid="state-listings">
      {items.map((it) => (
        <li key={it.id}>
          <Link to={`/listing/${it.id}`} data-testid={`state-listing-${it.id}`} className="block rounded-lg border p-3 hover:border-primary hover:-translate-y-0.5 transition-transform">
            <div className="text-xs text-muted-foreground">{CATEGORIES.find((c) => c.value === it.category)?.label}</div>
            <div className="font-medium line-clamp-1">{it.title}</div>
            {it.capacity_mw ? <div className="text-xs text-primary">{it.capacity_mw} MW</div> : null}
          </Link>
        </li>
      ))}
    </ul>
  );
};

const TopStates = ({ counts, onSelect }) => {
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
  if (!top.length) return <p className="text-sm text-muted-foreground">No listings yet. Click any state to explore.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {top.map(([s, n]) => (
        <button key={s} onClick={() => onSelect(s)} data-testid={`top-state-${slug(s)}`} className="rounded-full border px-3 py-1 text-sm hover:border-primary hover:text-primary transition-colors">
          {s} <span className="text-muted-foreground">· {n}</span>
        </button>
      ))}
    </div>
  );
};

export default function MapExplore() {
  const [category, setCategory] = useState("all");
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(null);
  const { user } = useAuth();
  const isMember = Boolean(user && user.email);
  const panelRef = useRef(null);

  useEffect(() => {
    const params = category !== "all" ? { category } : {};
    api.get("/listings/state-counts", { params })
      .then(({ data }) => setCounts(data))
      .catch(() => setCounts({}));
  }, [category]);

  useEffect(() => {
    if (selected && window.innerWidth < 1024) {
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selected]);

  const browseHref = `/browse?state=${encodeURIComponent(selected || "")}${category !== "all" ? `&category=${category}` : ""}`;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10" data-testid="map-page">
      <div className="label-caps">Explore by map</div>
      <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-primary tracking-tight mt-1">Opportunities across India</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">Click a state to see its renewable projects, land, connectivity and short-term power listings.</p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-72">
        <Select value={selected || ""} onValueChange={(v) => setSelected(v)}>
          <SelectTrigger data-testid="map-state-select"><SelectValue placeholder="Or pick a state / UT" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
        <Link to="/dashboard/new">
          <Button className="bg-primary hover:bg-primary/90 gap-2" data-testid="map-post-listing-btn">
            <Plus className="h-4 w-4" /> Post a listing
          </Button>
        </Link>
      </div>

      <ToggleGroup type="single" value={category} onValueChange={(v) => v && setCategory(v)} className="justify-start flex-wrap gap-1 mt-6" data-testid="map-category-toggle">
        <ToggleGroupItem value="all" data-testid="map-cat-all">All</ToggleGroupItem>
        {CATEGORIES.map((c) => (
          <ToggleGroupItem key={c.value} value={c.value} data-testid={`map-cat-${c.value}`}>{c.label}</ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div className="mt-6 grid lg:grid-cols-5 gap-8">
        <Card className="lg:col-span-3"><CardContent className="p-4">
          <IndiaMap counts={counts} selected={selected} onSelect={setSelected} />
          <div className="flex items-center gap-3 text-xs text-muted-foreground px-2">
            <span className="h-3 w-3 rounded-sm bg-[hsl(150_15%_90%)]" /> None
            <span className="h-3 w-3 rounded-sm bg-[hsl(160_70%_70%)]" /> Few
            <span className="h-3 w-3 rounded-sm bg-[hsl(160_70%_40%)]" /> Many
          </div>
        </CardContent></Card>

        <aside ref={panelRef} className="lg:col-span-2 space-y-4 scroll-mt-24">
          {selected ? (
            <Card className="border-primary/30"><CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /><h2 className="font-display text-2xl font-bold text-primary" data-testid="selected-state">{selected}</h2></div>
                <Badge variant="outline" data-testid="selected-state-count">{counts[selected] || 0} listings</Badge>
              </div>
              <div className="mt-4">{isMember ? <StateListings state={selected} category={category} /> : <SignUpPrompt state={selected} />}</div>
              <div className="mt-4 flex gap-2">
                {isMember && <Link to={browseHref} className="flex-1"><Button className="w-full bg-primary hover:bg-primary/90 gap-2" data-testid="view-state-browse">View all in {selected} <ArrowRight className="h-4 w-4" /></Button></Link>}
                <Button variant="outline" onClick={() => setSelected(null)} data-testid="clear-state">Clear</Button>
              </div>
            </CardContent></Card>
          ) : (
            <Card><CardContent className="p-6">
              <h2 className="font-display text-xl font-semibold text-primary">Top states</h2>
              <div className="mt-3"><TopStates counts={counts} onSelect={setSelected} /></div>
            </CardContent></Card>
          )}
        </aside>
      </div>
    </div>
  );
}
